import type { TenantAgent } from '../../services/tenantAgentsService';

/**
 * Where an agent lives in the address bar, and back.
 *
 * A workspace usually has one agent of a kind, and it lives at its kind:
 * /agents/policy_servicing/live. A kind it may have several of (Policy
 * Servicing desks for different agencies or lines of business) gives each
 * after the first its kind and id: /agents/policy_servicing--<id>/live. The
 * kind alone still opens the first one, the oldest, as the server does, so
 * every link written before a second agent existed opens what it always did.
 *
 * Never the bare id: an id alone is a saved analysis's address
 * (`playbookId.ts`), and the kind has to be readable from the address so the
 * sidebar and breadcrumbs know it without looking anything up.
 */

const SEPARATOR = '--';

const oldestFirst = (a: TenantAgent, b: TenantAgent) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id);

/** The kind an address is for: "policy_servicing" from "policy_servicing--<id>" or from itself. */
export function kindOfAddress(address: string): string {
  return address.split(SEPARATOR)[0];
}

/** The agent an address names: by its id, else the oldest ready one of its kind. */
export function resolveAgent(agents: TenantAgent[], address: string | undefined): TenantAgent | null {
  if (!address) return null;
  const [kind, id] = address.split(SEPARATOR);
  if (id) return agents.find((a) => a.id === id && a.kind === kind && a.ready) ?? null;
  return agents.filter((a) => a.kind === kind && a.ready).sort(oldestFirst)[0] ?? null;
}

/** The address an agent is opened at: its kind when it is the first of its kind, else kind and id. */
export function addressOf(agent: TenantAgent, agents: TenantAgent[]): string {
  const first = agents.filter((a) => a.kind === agent.kind).sort(oldestFirst)[0];
  return first && first.id !== agent.id ? `${agent.kind}${SEPARATOR}${agent.id}` : agent.kind;
}
